import React, { useState, useEffect, useContext } from "react";
import { AuthContext } from "../App";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Check, Crown, Sparkles, Zap, CreditCard, Building2, Smartphone } from "lucide-react";
import { useLanguage } from "../contexts/LanguageContext";

const Subscriptions = () => {
  const { token, user } = useContext(AuthContext);
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processingPlan, setProcessingPlan] = useState(null);
  const [userCurrency, setUserCurrency] = useState("INR");
  const [currencySymbol, setCurrencySymbol] = useState("₹");
  const [paymentDetails, setPaymentDetails] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [paymentMode, setPaymentMode] = useState("bank"); // bank, upi, gateway
  const [subscriptionEnabled, setSubscriptionEnabled] = useState(true);

  useEffect(() => {
    if (!user) {
      navigate("/");
      return;
    }
    fetchPlansForUser();
    fetchPaymentDetails();
  }, []);

  const fetchPlansForUser = async () => {
    try {
      // Fetch plans with automatic currency conversion
      const response = await axios.get(`${API}/subscription-plans/for-user`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const { plans: convertedPlans, user_currency, currency_symbol } = response.data;
      setPlans(convertedPlans || []);
      setUserCurrency(user_currency || "INR");
      setCurrencySymbol(currency_symbol || "₹");
      
      // Check if subscription management is enabled
      const settingsRes = await axios.get(`${API}/subscription-settings/public`);
      setSubscriptionEnabled(settingsRes.data.subscription_management_enabled);
    } catch (error) {
      console.error("Failed to fetch plans:", error);
      // Fallback to regular plans endpoint
      try {
        const fallbackRes = await axios.get(`${API}/subscription-plans`);
        setPlans(fallbackRes.data || []);
      } catch (e) {
        toast.error("Failed to load subscription plans");
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchPaymentDetails = async () => {
    // Payment details will be retrieved during checkout process
    // This prevents unauthorized access to admin endpoints
    setPaymentDetails(null);
  };

  const handleSubscribe = async (planId) => {
    const plan = plans.find(p => p.id === planId || p.plan_id === planId);
    setSelectedPlan(plan);
    setShowPaymentModal(true);
  };

  const confirmSubscription = async () => {
    try {
      const planIdentifier = selectedPlan.id || selectedPlan.plan_id;
      setProcessingPlan(planIdentifier);
      setShowPaymentModal(false);
      
      // Try direct activation first (for testing/demo without Stripe)
      try {
        const directResponse = await axios.post(
          `${API}/subscriptions/activate-direct`,
          null,
          {
            params: { plan_id: planIdentifier },
            headers: { Authorization: `Bearer ${token}` }
          }
        );
        
        if (directResponse.data.success) {
          toast.success(directResponse.data.message);
          setTimeout(() => navigate("/dashboard"), 1500);
          return;
        }
      } catch (directError) {
        // If direct fails, try Stripe checkout
        console.log("Direct activation failed, trying Stripe...", directError);
      }
      
      const response = await axios.post(
        `${API}/subscriptions/checkout`,
        null,
        {
          params: { plan_id: planIdentifier },
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.data.checkout_url) {
        // Redirect to Stripe checkout
        window.location.href = response.data.checkout_url;
      } else if (response.data.success) {
        // Free plan activated
        toast.success("Free plan activated!");
        setTimeout(() => navigate("/dashboard"), 1500);
      }
    } catch (error) {
      toast.error("Failed to process subscription");
      console.error(error);
    } finally {
      setProcessingPlan(null);
    }
  };

  const getPlanIcon = (planId) => {
    switch(planId) {
      case "free": return <Sparkles className="w-8 h-8 text-gray-500" />;
      case "premium": return <Crown className="w-8 h-8 text-blue-500" />;
      case "pro": return <Zap className="w-8 h-8 text-purple-500" />;
      default: return <Sparkles className="w-8 h-8" />;
    }
  };

  const getPlanColor = (planId) => {
    switch(planId) {
      case "free": return "border-gray-300";
      case "premium": return "border-blue-500 shadow-lg";
      case "pro": return "border-purple-500 shadow-xl";
      default: return "border-gray-300";
    }
  };

  const isCurrentPlan = (planId) => {
    return user?.subscription_tier === planId;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
      <Navbar />
      <div className="container mx-auto px-4 py-12">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold mb-4">Choose Your Plan</h1>
          <p className="text-xl text-gray-600 mb-2">
            Unlock your career potential with premium features
          </p>
          {user?.subscription_tier && (
            <Badge variant="outline" className="mt-2">
              Current Plan: {user.subscription_tier.toUpperCase()}
            </Badge>
          )}
        </div>

        {loading ? (
          <div className="text-center py-12">
            <p className="text-gray-600">Loading plans...</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {plans.map((plan) => (
              <Card 
                key={plan.id || plan.plan_id} 
                className={`relative transition-all hover:scale-105 ${getPlanColor(plan.id || plan.plan_id)} ${(plan.id || plan.plan_id) === 'premium' || plan.is_featured ? 'transform scale-105' : ''}`}
              >
                {((plan.id || plan.plan_id) === "premium" || plan.is_featured) && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <Badge className="bg-blue-500">Most Popular</Badge>
                  </div>
                )}
                
                <CardHeader className="text-center">
                  <div className="flex justify-center mb-4">
                    {getPlanIcon(plan.id || plan.plan_id)}
                  </div>
                  <CardTitle className="text-2xl">{plan.display_name || plan.name}</CardTitle>
                  <div className="mt-4">
                    {/* Show converted price if available */}
                    <span className="text-4xl font-bold">
                      {plan.formatted_price || `${currencySymbol}${plan.display_price || plan.price}`}
                    </span>
                    <span className="text-gray-600">/{plan.duration_type || 'month'}</span>
                  </div>
                  {plan.original_currency && plan.original_currency !== plan.display_currency && (
                    <p className="text-xs text-gray-500 mt-2">
                      Original: {plan.original_currency} {plan.original_price}
                    </p>
                  )}
                </CardHeader>
                
                <CardContent>
                  <ul className="space-y-3 mb-6">
                    {(plan.features || []).map((feature, idx) => (
                      <li key={idx} className="flex items-start gap-2">
                        <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                        <span className="text-sm">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  
                  <Button 
                    className="w-full"
                    variant={(plan.id || plan.plan_id) === "premium" || plan.is_featured ? "default" : "outline"}
                    onClick={() => handleSubscribe(plan.id || plan.plan_id)}
                    disabled={processingPlan === (plan.id || plan.plan_id) || isCurrentPlan(plan.id || plan.plan_id)}
                  >
                    {processingPlan === (plan.id || plan.plan_id) ? "Processing..." : 
                     isCurrentPlan(plan.id || plan.plan_id) ? "Current Plan" : 
                     (plan.price === 0 || plan.display_price === 0) ? "Get Started" : "Upgrade Now"}
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        <div className="mt-12 max-w-4xl mx-auto">
          <Card>
            <CardHeader>
              <CardTitle>Frequently Asked Questions</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <h4 className="font-semibold mb-2">Can I cancel anytime?</h4>
                <p className="text-sm text-gray-600">Yes, you can cancel your subscription at any time. Your access will continue until the end of your billing period.</p>
              </div>
              <div>
                <h4 className="font-semibold mb-2">What payment methods do you accept?</h4>
                <p className="text-sm text-gray-600">We accept all major credit cards, debit cards, and digital wallets through Stripe secure payment processing.</p>
              </div>
              <div>
                <h4 className="font-semibold mb-2">Can I upgrade or downgrade my plan?</h4>
                <p className="text-sm text-gray-600">Yes, you can change your plan at any time. Changes will be reflected in your next billing cycle.</p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Payment Details Modal */}
      <Dialog open={showPaymentModal} onOpenChange={setShowPaymentModal}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Complete Your Subscription</DialogTitle>
            <DialogDescription>
              {selectedPlan ? `You're subscribing to ${selectedPlan.plan_name}` : 'Select your payment method'}
            </DialogDescription>
          </DialogHeader>
          
          {selectedPlan && (
            <div className="space-y-4 py-4">
              <div className="bg-gray-100 p-4 rounded-lg">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-gray-700">Plan:</span>
                  <span className="font-semibold">{selectedPlan.plan_name}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-700">Amount:</span>
                  <span className="text-2xl font-bold text-green-600">
                    {currencySymbol}{selectedPlan.price_monthly || selectedPlan.price}/month
                  </span>
                </div>
              </div>

              {/* Payment Mode Selection */}
              <div>
                <Label className="mb-3 block font-semibold">Select Payment Method</Label>
                <RadioGroup value={paymentMode} onValueChange={setPaymentMode} className="space-y-3">
                  {paymentDetails?.type === 'bank' && (
                    <div className="flex items-center space-x-2 border rounded-lg p-3 cursor-pointer hover:bg-gray-50">
                      <RadioGroupItem value="bank" id="bank" />
                      <Label htmlFor="bank" className="flex items-center gap-2 cursor-pointer flex-1">
                        <Building2 className="w-5 h-5 text-blue-600" />
                        <span>Bank Transfer</span>
                      </Label>
                    </div>
                  )}
                  {paymentDetails?.type === 'upi' && (
                    <div className="flex items-center space-x-2 border rounded-lg p-3 cursor-pointer hover:bg-gray-50">
                      <RadioGroupItem value="upi" id="upi" />
                      <Label htmlFor="upi" className="flex items-center gap-2 cursor-pointer flex-1">
                        <Smartphone className="w-5 h-5 text-green-600" />
                        <span>UPI Payment</span>
                      </Label>
                    </div>
                  )}
                  <div className="flex items-center space-x-2 border rounded-lg p-3 cursor-pointer hover:bg-gray-50">
                    <RadioGroupItem value="gateway" id="gateway" />
                    <Label htmlFor="gateway" className="flex items-center gap-2 cursor-pointer flex-1">
                      <CreditCard className="w-5 h-5 text-purple-600" />
                      <span>Payment Gateway (Card/Wallet)</span>
                    </Label>
                  </div>
                </RadioGroup>
              </div>

              {/* Payment Details Based on Selection */}
              {paymentMode === 'bank' && paymentDetails?.type === 'bank' && (
                <Card className="bg-blue-50 border-blue-200">
                  <CardContent className="pt-4">
                    <h4 className="font-semibold mb-3 text-blue-900">Bank Account Details</h4>
                    <div className="text-sm space-y-2">
                      <div className="flex justify-between">
                        <span className="text-gray-700">Bank Name:</span>
                        <span className="font-medium">{paymentDetails.bank_name}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-700">Account Number:</span>
                        <span className="font-medium">{paymentDetails.account_number}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-700">IFSC Code:</span>
                        <span className="font-medium">{paymentDetails.ifsc_code}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-700">Account Holder:</span>
                        <span className="font-medium">{paymentDetails.account_holder_name}</span>
                      </div>
                    </div>
                    <p className="text-xs text-blue-700 mt-3">
                      Transfer the amount to above account and click Confirm.
                    </p>
                  </CardContent>
                </Card>
              )}

              {paymentMode === 'upi' && paymentDetails?.type === 'upi' && (
                <Card className="bg-green-50 border-green-200">
                  <CardContent className="pt-4">
                    <h4 className="font-semibold mb-3 text-green-900">UPI Payment Details</h4>
                    <div className="text-sm space-y-2">
                      <div className="flex justify-between">
                        <span className="text-gray-700">UPI ID:</span>
                        <span className="font-medium">{paymentDetails.upi_id}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-700">Merchant Name:</span>
                        <span className="font-medium">{paymentDetails.merchant_name}</span>
                      </div>
                    </div>
                    <p className="text-xs text-green-700 mt-3">
                      Pay using any UPI app (GPay, PhonePe, Paytm) and click Confirm.
                    </p>
                  </CardContent>
                </Card>
              )}

              {paymentMode === 'gateway' && (
                <Card className="bg-purple-50 border-purple-200">
                  <CardContent className="pt-4">
                    <h4 className="font-semibold mb-3 text-purple-900">Payment Gateway</h4>
                    <p className="text-sm text-gray-700 mb-3">
                      You'll be redirected to secure payment gateway to complete the transaction using Credit/Debit Card or Digital Wallets.
                    </p>
                    <div className="flex gap-2 text-xs text-gray-600">
                      <Badge variant="outline">Visa</Badge>
                      <Badge variant="outline">Mastercard</Badge>
                      <Badge variant="outline">RuPay</Badge>
                      <Badge variant="outline">Paytm</Badge>
                    </div>
                  </CardContent>
                </Card>
              )}

              {!paymentDetails && paymentMode !== 'gateway' && (
                <div className="bg-yellow-50 border border-yellow-200 p-3 rounded-lg text-sm text-yellow-800">
                  Payment details not configured. Please select Payment Gateway or contact admin.
                </div>
              )}
            </div>
          )}
          
          <div className="flex gap-2">
            <Button onClick={confirmSubscription} className="flex-1" disabled={processingPlan}>
              {processingPlan ? "Processing..." : paymentMode === 'gateway' ? "Proceed to Payment" : "Confirm Payment"}
            </Button>
            <Button onClick={() => setShowPaymentModal(false)} variant="outline">Cancel</Button>
          </div>
        </DialogContent>
      </Dialog>
      <Footer />
    </div>
  );
};

export default Subscriptions;
